import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { CLOSE_CAMPAIGN_FORM, errorTypes, statusCampaign } from '@/helpers/constants';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo, Language, RouterParams } from '@/types';
import ConfirmDialog from '../../UI/Dialog/ConfirmDialog';
import InputText from '../../UI/InputText/InputText';
import styles from '../CampaignHeader.module.scss';

interface CloseCampaignDialogProps {
  campaignInfo: CampaignInfo;
  onClosePopup: (refresh?: boolean) => void;
  lang: Language;
}

const LABELS = {
  COMMENT: 'comment',
  TRANSLATION: 'translation',
};

const CloseCampaignDialog: React.FC<CloseCampaignDialogProps> = (props) => {
  const { campaignInfo, onClosePopup, lang } = props;
  const { id } = useParams<RouterParams>();
  const [closeForm, setCloseForm] = useState(CLOSE_CAMPAIGN_FORM);
  const { t } = useTranslation();
  const { addToast, closeToasts } = useToastContext();
  const defaultLang = campaignInfo.defaultLanguage;
  const { detectLanguage } = useDetectLanguage();

  const showTranslationComment = (
    (campaignInfo.status !== statusCampaign.IN_CREATION || campaignInfo.version > 1) && campaignInfo.activeLanguages?.length > 1
  );

  const displayInput = (label: string) => (label === LABELS.COMMENT || (label === LABELS.TRANSLATION && showTranslationComment));

  const handleCloseFormChange = (label: string, value: string) => {
    if (displayInput(label)) {
      const newState = {
        ...closeForm,
        [label]: {
          value,
          errors: { required: !value, min: value && value.length < 8, max: value && value.length === 256 },
          touched: true,
        },
      };

      newState[label].errors.invalid = ['required', 'min', 'max'].some((type) => newState[label].errors[type]);
      newState.invalid = ![LABELS.COMMENT, ...(showTranslationComment ? [LABELS.TRANSLATION] : [])]
        .every((input) => !newState[input].errors.invalid && newState[input].touched);
      setCloseForm(newState);
    }
  };

  const closeDialog = () => {
    setCloseForm(CLOSE_CAMPAIGN_FORM);
    onClosePopup();
    closeToasts(errorTypes.LANGUAGE_WARNING_TRANSLATIONS);
  };

  const onCloseCampaign = () => {
    campaignServices.closeCampaign(id, {
      comment: { [defaultLang]: closeForm.comment.value, [lang]: closeForm.translation.value },
    }).then(() => {
      onClosePopup(true);
      closeToasts(errorTypes.LANGUAGE_WARNING_TRANSLATIONS);
    })
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  return (
    <ConfirmDialog
      close={closeDialog}
      confirm={onCloseCampaign}
      title={t('campaign.messageClosingCR')}
      closeBtnText={t('dialog.cancel')}
      confirmBtnText={t('dialog.confirm')}
      confirmDisabled={closeForm.invalid}
    >
      <form className={styles.OneColumns}>
        {closeForm.fields.map(({ name, label }) => (
          displayInput(name)
            ? (
              <>
                <InputText
                  key={name}
                  invalid={closeForm[name].errors.invalid}
                  multiline
                  rows={4}
                  maxLength={256}
                  id={`close-campaign-form-input-${name}`}
                  data-testid={`close-campaign-form-input-${name}`}
                  label={t(label, { lang: t(`languageChip.${defaultLang === 'EN' ? 'DE' : 'EN'}`) })}
                  value={closeForm[name].value}
                  handleChange={(ev) => { handleCloseFormChange(name, ev.target.value); }}
                  onBlur={(ev) => detectLanguage(ev.target.value, name === LABELS.TRANSLATION)}
                  formControlClass={styles.CloseCRInput}
                  helperText={[
                    ...((closeForm[name].errors.required)
                      ? [t('validations.required')]
                      : []),
                    ...((closeForm[name].errors.min)
                      ? [t('validations.commentsMinLength')]
                      : []),
                    ...((closeForm[name].errors.max)
                      ? [t('validations.maxLength', { maxLength: 256 })]
                      : []),
                  ]}
                />
              </>
            )
            : ''
        ))}
      </form>
    </ConfirmDialog>
  );
};

export default CloseCampaignDialog;
